/**
 * Theme composable for runtime branding.
 *
 * Fetches the global branding config (colors + logos) and applies it to the
 * live UI by overriding CSS custom properties and the Vuetify runtime theme.
 * Also exposes reactive logo image sources with static asset fallbacks.
 */

import defaultLogo from '@assets/images/allocore_logo.svg';
import defaultFooterLogo from '@assets/images/allocore-a.png';
import vuetify from '@plugins/vuetify';
import { type ThemeColors, ThemeConfigService, type ThemeImages, type ThemeText } from '@services/ThemeConfigService';
import { computed, shallowRef } from 'vue';

/** Default organization name shown in the footer when none is configured. */
const DEFAULT_ORGANIZATION_NAME = 'USDA';

/** Maps a logical color name to the CSS custom properties it should drive. */
const CSS_VARIABLE_MAP: Record<string, string[]> = {
  primary: ['--color-primary', '--bs-primary'],
  secondary: ['--color-secondary', '--bs-secondary'],
  accent: ['--color-tertiary'],
  footerGradientStart: ['--footer-gradient-start'],
  footerGradientEnd: ['--footer-gradient-end'],
};

/** Maps a logical color name to the Vuetify theme color it should drive. */
const VUETIFY_COLOR_MAP: Record<string, string> = {
  primary: 'primary',
  secondary: 'secondary',
  accent: 'accent',
};

const themeInstance = shallowRef<ReturnType<typeof createThemeInstance> | null>(null);

function createThemeInstance() {
  const service = new ThemeConfigService();

  const colors = shallowRef<ThemeColors>({});
  const images = shallowRef<ThemeImages>({});
  const text = shallowRef<ThemeText>({});
  const isLoaded = shallowRef(false);

  const navbarLogo = computed(() => images.value.navbarLogo || defaultLogo);
  const footerLogo = computed(() => images.value.footerLogo || defaultFooterLogo);
  const loginLogo = computed(() => images.value.loginLogo || defaultLogo);
  const organizationName = computed(() => text.value.organizationName || DEFAULT_ORGANIZATION_NAME);

  const applyColors = (nextColors: ThemeColors): void => {
    const root = document.documentElement;
    const vuetifyColors = vuetify.theme.themes.value.light?.colors;

    for (const [name, value] of Object.entries(nextColors)) {
      if (!value) {
        continue;
      }

      for (const cssVar of CSS_VARIABLE_MAP[name] ?? []) {
        root.style.setProperty(cssVar, value);
      }

      const vuetifyKey = VUETIFY_COLOR_MAP[name];
      if (vuetifyKey && vuetifyColors) {
        vuetifyColors[vuetifyKey] = value;
      }
    }
  };

  const applyFavicon = (href: string): void => {
    let link = document.querySelector<HTMLLinkElement>('link[rel~="icon"]');
    if (!link) {
      link = document.createElement('link');
      link.rel = 'icon';
      document.head.appendChild(link);
    }
    link.href = href;
  };

  /**
   * Fetch the branding config and apply it. Silently no-ops on failure so a
   * missing/erroring config never blocks the app from rendering.
   */
  const loadTheme = async (): Promise<void> => {
    const res = await service.get();
    if (!(res.success && res.response)) {
      isLoaded.value = true;
      return;
    }

    colors.value = res.response.colors ?? {};
    images.value = res.response.images ?? {};
    text.value = res.response.text ?? {};

    applyColors(colors.value);
    if (images.value.favicon) {
      applyFavicon(images.value.favicon);
    }

    isLoaded.value = true;
  };

  return {
    colors,
    images,
    text,
    isLoaded,
    navbarLogo,
    footerLogo,
    loginLogo,
    organizationName,
    loadTheme,
  };
}

export function useTheme() {
  if (!themeInstance.value) {
    themeInstance.value = createThemeInstance();
  }
  return themeInstance.value;
}
